/**
 * origins — canonical URLs for LinkLane's subdomain split.
 *
 * One SPA build is served on both `linklane.io` (marketing) and
 * `app.linklane.io` (application). These helpers let the same build behave
 * correctly per host (cross-subdomain links, host-aware SEO) without forcing
 * redirects. On localhost / Vercel previews neither host matches, so the split
 * is inert and everything stays single-origin (current dev behavior).
 *
 * Override per environment with REACT_APP_MARKETING_URL / _APP_URL / _SHORT_URL.
 */
export const MARKETING_URL = (process.env.REACT_APP_MARKETING_URL || "https://linklane.io").replace(
  /\/$/,
  ""
);
export const APP_URL = (process.env.REACT_APP_APP_URL || "https://app.linklane.io").replace(
  /\/$/,
  ""
);
export const SHORT_URL = (process.env.REACT_APP_SHORT_URL || "https://lny.me").replace(/\/$/, "");

function hostOf(url) {
  try {
    return new URL(url).hostname.toLowerCase();
  } catch {
    return "";
  }
}

export const MARKETING_HOST = hostOf(MARKETING_URL);
export const APP_HOST = hostOf(APP_URL);
export const SHORT_HOST = hostOf(SHORT_URL);

function currentHost() {
  return typeof window !== "undefined" ? window.location.hostname.toLowerCase() : "";
}

/** True when actually served on the production app subdomain. */
export const isAppHost = () => currentHost() === APP_HOST;
/** True when actually served on the production marketing root. */
export const isMarketingHost = () => currentHost() === MARKETING_HOST;
/** True when served on the dedicated short-link host (e.g. lny.me). */
export const isShortHost = () => currentHost() === SHORT_HOST;
/** The split is only "live" on the real prod hosts; localhost/preview = single origin. */
export const splitActive = () => isAppHost() || isMarketingHost();

/** Absolute URL on the app origin (for cross-subdomain links from marketing). */
export const appUrl = (path = "/") => `${APP_URL}${path.startsWith("/") ? path : `/${path}`}`;
/** Absolute URL on the marketing origin (for "back to site" links from the app). */
export const marketingUrl = (path = "/") =>
  `${MARKETING_URL}${path.startsWith("/") ? path : `/${path}`}`;

/**
 * Link props for a target that belongs on the APP origin. Returns a relative
 * `{ to }` when already on the app host (or split inert, e.g. localhost) so SPA
 * routing stays fast; returns absolute `{ href }` only when a real cross-
 * subdomain hop is needed (we're on marketing). Spread onto a Link/<a>-aware
 * component (CandyButton accepts both) or use with OriginLink.
 */
export const appLink = (path = "/") =>
  splitActive() && !isAppHost() ? { href: appUrl(path) } : { to: path };

/** Link props for a target on the MARKETING origin (mirror of appLink). */
export const marketingLink = (path = "/") =>
  splitActive() && !isMarketingHost() ? { href: marketingUrl(path) } : { to: path };
